import React from 'react';
import { EnhancedDimensionScores } from '../types/quiz';

interface DimensionChartProps {
  dimensionScores: EnhancedDimensionScores;
  childName: string;
}

const DIMENSION_LABELS: Record<string, string> = {
  sensory: 'Sensory Support',
  structure: 'Structure & Predictability',
  pacing: 'Individualized Pacing',
  confidence: 'Emotional Safety',
  interest: 'Interest-Driven Learning'
};

const DIMENSION_COLORS: Record<string, string> = {
  confidence: '#3B82F6',  // Blue - Emotional Safety
  pacing: '#8B5CF6',      // Purple - Individualized Pacing
  structure: '#6366F1',   // Indigo - Structure & Predictability
  sensory: '#14B8A6',     // Teal - Sensory Support
  interest: '#F97316'     // Orange - Interest-Driven Learning
};

const DimensionChart: React.FC<DimensionChartProps> = ({ dimensionScores, childName }) => {
  // Convert scores to array and sort by percentage descending (highest priority first)
  const sortedDimensions = Object.entries(dimensionScores.scores)
    .map(([dimension, score]) => ({
      dimension,
      label: DIMENSION_LABELS[dimension] || dimension,
      score,
      possible: dimensionScores.possible[dimension as keyof typeof dimensionScores.possible],
      percentage: dimensionScores.percentages[dimension as keyof typeof dimensionScores.percentages]
    }))
    .sort((a, b) => b.percentage - a.percentage);

  // Generate visual bar for each dimension
  const generateBar = (score: number, possible: number, isTop2: boolean, color: string) => {
    const percentage = possible > 0 ? (score / possible) * 100 : 0;
    const filledBlocks = Math.round((percentage / 100) * 20); // 20 blocks total
    const emptyBlocks = 20 - filledBlocks;

    const filled = '█'.repeat(filledBlocks);
    const empty = '░'.repeat(emptyBlocks);

    return (
      <span className={`font-mono ${isTop2 ? 'font-bold' : ''}`}>
        <span style={{ color }}>{filled}</span>
        <span className="text-gray-300">{empty}</span>
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 md:p-7 mb-6">
      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-4 md:mb-6">
        {childName.toUpperCase()}'S UNIQUE NEEDS PROFILE
      </h2>

      <div className="space-y-2 md:space-y-3 font-mono text-xs md:text-sm">
        {sortedDimensions.map((dim, index) => {
          const isTop2 = index < 2;
          const color = DIMENSION_COLORS[dim.dimension] || '#6B7280';

          return (
            <div key={dim.dimension} className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="flex-1">
                {generateBar(dim.score, dim.possible, isTop2, color)}
              </div>
              <div className={`flex-shrink-0 text-left sm:text-right ${isTop2 ? 'font-bold' : ''}`}>
                <span className="text-gray-900">{dim.label}:</span>{' '}
                <span className="text-gray-700">
                  {dim.score}/{dim.possible} ({dim.percentage}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-sm md:text-base text-gray-700 font-semibold mt-4 md:mt-6">
        {childName}'s top 2 needs: {sortedDimensions[0].label} + {sortedDimensions[1].label}
      </p>
    </div>
  );
};

export default DimensionChart;
